import { useState, useEffect } from "react";
import { useRecoilStateLoadable } from "recoil";
import _ from "lodash";
import {
  ICertificateInfo,
  IDatePickerType,
  IUser,
  IUserProgramCertificate,
  OnboardingCertificateIssueDate,
  OnboardingDatePickerType,
  IOptionalStandard,
  ProgramData,
  ProgramCertificate,
  ProgramRole,
  IUserProgramRoles,
  CompetenceActivityClass,
  IUserProgramRolesComplianceData,
} from "@interfaces/Interfaces";
import * as ProgramUtils from "@utils/programUtils";
import {
  getMandatorySecondaryRoleNames,
  getValidSecondaryRoleNamesForPrimary,
} from "@utils/programDetails/programDetailsValidation";
import { userAtom } from "@state/State";

function mapStoredRolesToSelectedNames(
  stored: IUserProgramRoles | undefined,
  programID: string | undefined
): string[] {
  if (!stored?.programRoles?.length) {
    return [];
  }

  if (programID && ProgramUtils.isEPRR(programID)) {
    return stored.programRoles.map((item) => item.roleName);
  }

  return stored.programRoles ?? [];
}

function mapStoredSecondaryRolesToSelectedNames(
  stored: IUserProgramRoles | undefined,
  programID: string | undefined
): string[] {
  if (!stored?.secondaryRoles?.length) {
    return [];
  }

  if (programID && ProgramUtils.isEPRR(programID)) {
    return stored.secondaryRoles.map((item) => item.roleName);
  }

  return stored.secondaryRoles ?? [];
}

export const useProgramDetailsState = (program: ProgramData) => {
  // User state
  const [user, setUser] = useRecoilStateLoadable<IUser | null>(userAtom);

  // Program data state
  const [allCertificates, setAllCertificates] = useState<ProgramCertificate[]>([]);
  const [allRoles, setAllRoles] = useState<ProgramRole[]>([]);

  // Selection state
  const [selectedPrimaryRoles, setSelectedPrimaryRoles] = useState<string[]>([]);
  const [selectedSecondaryRoles, setSelectedSecondaryRoles] = useState<string[]>([]);
  const [selectedStandards, setSelectedStandards] = useState<IOptionalStandard[]>([]);
  const [declarationChecked, setDeclarationChecked] = useState<boolean>(false);
  const [selectedArea, setSelectedArea] = useState<string>(getInitialArea);
  const [selectedTeam, setSelectedTeam] = useState<string>(getInitialTeam);
  const [nqpCourse, setNQPCourse] = useState<string>(getInitialNQPCourseId);
  const [complianceData, setComplianceData] = useState<IUserProgramRolesComplianceData[] | null>(null);

  // Certificate state
  const [uploads, setUploads] = useState<ICertificateInfo[]>([]);
  const [deletions, setDeletions] = useState<ICertificateInfo[]>([]);
  const [existingCertificates, setExistingCertificates] = useState<IUserProgramCertificate[]>([]);
  const [certificateType, setCertificateType] = useState<string>("");
  const [selectingCertificate, setSelectingCertificate] = useState<boolean>(false);
  const [deletingCertificate, setDeletingCertificate] = useState<boolean>(false);
  const [issueDates, setIssueDates] = useState<OnboardingCertificateIssueDate[]>([]);
  const [expiryDates, setExpiryDates] = useState<OnboardingCertificateIssueDate[]>([]);

  // Date picker state
  const [appraisalDate, setAppraisalDate] = useState<Date | null>(null);
  const [appraisalDatePickerVisible, setAppraisalDatePickerVisible] = useState<boolean>(false);

  // User start date state
  const [startDate, setStartDate] = useState<Date | null>(getInitialStartDate);
  const [startDatePickerVisible, setStartDatePickerVisible] = useState<boolean>(false);

  const [datePickerVisible, setDatePickerVisible] = useState<boolean>(false);
  const [datePickerType, setDatePickerType] = useState<string>(IDatePickerType.ISSUE);
  const [datePickerValue, setDatePickerValue] = useState<Date>(new Date());
  const [newDateInfo, setNewDateInfo] = useState<(OnboardingCertificateIssueDate & OnboardingDatePickerType) | null>(
    null
  );
  const [changingDate, setChangingDate] = useState<boolean>(false);

  // UI state
  const [saving, setSaving] = useState<boolean>(false);

  function getInitialStartDate(): Date | null {
    if (user.state === "hasValue" && user.contents) {
      const role = user.contents.programRoles?.find((role) => role.programID === program.ID);
      if (role && role?.startDate) {
        return new Date(role.startDate);
      }
    }
    return null;
  }

  function getInitialNQPCourseId(): string {
    if (user.state === "hasValue" && user.contents) {
      const role = user.contents.programRoles?.find((role) => role.programID === program.ID);
      if (role && role?.courseId) {
        return role.courseId;
      }
    }
    return "";
  }

  function getInitialTeam(): string {
    if (user.state === "hasValue" && user.contents) {
      const role = user.contents.programRoles?.find((role) => role.programID === program.ID);
      if (role && role?.team) {
        return role.team;
      }
    }

    return "";
  }

  function getInitialArea(): string {
    if (user.state === "hasValue" && user.contents) {
      const role = user.contents.programRoles?.find((role) => role.programID === program.ID);
      if (role && role?.area) {
        return role.area;
      }
    }

    return "";
  }

  // Load program data when program changes
  useEffect(() => {
    const getProgramData = (): void => {
      if (program) {
        if (program.Roles) {
          setAllRoles(_.orderBy(program.Roles, ["Name"], ["asc"]));
        }
      }
    };

    getProgramData();
  }, [program?.version]);

  // Initialize roles and standards from user data
  useEffect(() => {
    const setInitialRoles = (): void => {
      if (user.contents) {
        const roles: IUserProgramRoles[] = _.cloneDeep(user.contents?.programRoles);

        if (roles) {
          const programRoles = roles.find((item) => item.programName === program.Name);

          if (programRoles) {
            const primaryRoles = mapStoredRolesToSelectedNames(programRoles, programRoles.programID).filter(
              (name) => !program.Roles?.find((role: ProgramRole) => role.Name === name)?.AppearAsSecondaryRoleOnly
            );
            const storedSecondaryRoles = mapStoredSecondaryRolesToSelectedNames(
              programRoles,
              programRoles.programID
            );
            const mandatorySecondaryRoles = getMandatorySecondaryRoleNames(primaryRoles, program);
            const validSecondaryRoleNames = getValidSecondaryRoleNamesForPrimary(primaryRoles, program);
            const optionalSecondaryRoles = storedSecondaryRoles.filter(
              (name) => name && !mandatorySecondaryRoles.includes(name) && validSecondaryRoleNames.has(name)
            );

            setSelectedPrimaryRoles(primaryRoles);
            setSelectedSecondaryRoles([...mandatorySecondaryRoles, ...optionalSecondaryRoles]);

            // set initial standards
            if (ProgramUtils.isEPRR(programRoles.programID!) && programRoles.programRoles.length) {
              const matchedRole = program.Roles?.find(
                (role: ProgramRole) => role.ID === programRoles.programRoles[0].roleID
              );

              if (!matchedRole) {
                setSelectedStandards([]);
              } else {
                const ids: string[] = programRoles.programRoles[0].optionalStandards.map(
                  (standard: { id: string }) => standard.id
                );
                const optionalActivity = matchedRole.OptionalCompetenceActivity;
                let preSelectedStandards: CompetenceActivityClass[] | undefined;

                if (optionalActivity && Array.isArray(optionalActivity)) {
                  if (optionalActivity.length > 0 && typeof optionalActivity[0] === "object") {
                    const activityArray = optionalActivity as CompetenceActivityClass[];
                    preSelectedStandards = activityArray.filter(
                      (item: CompetenceActivityClass) => "ID" in item && ids.includes(item.ID)
                    );
                  }
                }

                const mapped: IOptionalStandard[] =
                  preSelectedStandards?.map((item: CompetenceActivityClass) => ({
                    id: item.ID,
                    name: item.Name,
                    description: item.Description || "",
                    learningObjectives: [],
                  })) ?? [];
                setSelectedStandards(mapped);
              }
            } else if (!ProgramUtils.isEPRR(programRoles.programID!)) {
              setSelectedArea(programRoles.area ?? "");
            }
          }
        }
      }
    };

    if (user.state === "hasValue" && program) {
      setInitialRoles();
    }
  }, [user?.contents?.userData?.contactID, program]);

  // Initialize compliance data from user data

  useEffect(() => {
    const setInitialComplianceData = (): void => {
      if (user.contents) {
        const roles: IUserProgramRoles[] = _.cloneDeep(user.contents?.programRoles);

        if (roles) {
          const programRoles = roles.find((item) => item.programName === program.Name);

          if (programRoles) {
            if (programRoles.complianceData) {
              setComplianceData(programRoles.complianceData);
            }
          }
        }
      }
    };

    if (user.state === "hasValue" && program) {
      setInitialComplianceData();
    }
  }, [user, program]);

  // Initialize area from user data
  useEffect(() => {
    const setInitialArea = (): void => {
      if (user.contents) {
        const roles: IUserProgramRoles[] = _.cloneDeep(user.contents?.programRoles);

        if (roles) {
          const programRoles = roles.find((item) => item.programName === program.Name);

          if (programRoles) {
            if (programRoles.area) {
              setSelectedArea(programRoles.area!);
            }
          }
        }
      }
    };

    if (user.state === "hasValue" && program) {
      setInitialArea();
    }
  }, [user, program]);

  return {
    // User state
    user,
    setUser,

    // Program data
    allRoles,
    setAllRoles,
    allCertificates,
    setAllCertificates,

    // Form state
    selectedPrimaryRoles,
    setSelectedPrimaryRoles,
    selectedSecondaryRoles,
    setSelectedSecondaryRoles,
    selectedStandards,
    setSelectedStandards,
    selectedArea,
    setSelectedArea,
    selectedTeam,
    setSelectedTeam,
    declarationChecked,
    setDeclarationChecked,
    nqpCourse,
    complianceData,
    setComplianceData,

    // Certificate state
    uploads,
    setUploads,
    deletions,
    setDeletions,
    existingCertificates,
    setExistingCertificates,
    certificateType,
    setCertificateType,
    selectingCertificate,
    setSelectingCertificate,
    deletingCertificate,
    setDeletingCertificate,
    setNQPCourse,

    issueDates,
    setIssueDates,
    expiryDates,
    setExpiryDates,

    // Date picker state
    appraisalDate,
    setAppraisalDate,
    appraisalDatePickerVisible,
    setAppraisalDatePickerVisible,
    datePickerVisible,
    setDatePickerVisible,
    datePickerType,
    setDatePickerType,
    datePickerValue,
    setDatePickerValue,
    newDateInfo,
    setNewDateInfo,
    changingDate,
    setChangingDate,
    startDate,
    setStartDate,
    startDatePickerVisible,
    setStartDatePickerVisible,

    // UI state
    saving,
    setSaving,
  };
};
